import type { SteeringPlan } from '../data/play/types';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { usePrelaunch } from '../data/launch';
import type { WorldView } from '../data/types';
import { GameRenderer } from './GameRenderer';
import { usePrefs } from '../app/prefs';

/**
 * Canvas host for the overhead world. Spectator pause stops ANIMATION only — the shared game keeps going.
 * A visually hidden live region narrates canonical events for screen readers.
 */
export interface WorldCanvasHandle {
  /** Play the current (finished) run's verified steps again from the start line. */
  replay(): void;
}

export const WorldCanvas = forwardRef<WorldCanvasHandle, { view: WorldView; className?: string; label?: string; ambient?: boolean; steeringPreview?: SteeringPlan | null; onAim?: (x: number) => void; pausedLabel?: string }>(function WorldCanvas({ view, className, label, ambient = false, steeringPreview, onAim, pausedLabel = 'Animation paused — the shared game continues' }, handle) {
  const prelaunch = usePrelaunch();
  const ref = useRef<HTMLCanvasElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const renderer = useRef<GameRenderer | null>(null);
  const { reducedMotion, paused, labels } = usePrefs();
  const [announce, setAnnounce] = useState('');
  useImperativeHandle(handle, () => ({ replay: () => renderer.current?.replay() }), []);

  useEffect(() => {
    const c = ref.current!;
    const r = new GameRenderer(c, { reducedMotion, showLabels: labels, ambient, prelaunch });
    renderer.current = r;
    const ro = new ResizeObserver(() => {
      const b = wrap.current!.getBoundingClientRect();
      r.resize(Math.max(1, b.width), Math.max(1, b.height), window.devicePixelRatio);
    });
    ro.observe(wrap.current!);
    return () => {
      ro.disconnect();
      renderer.current = null;
    };
  }, []);

  useEffect(() => {
    if (renderer.current) renderer.current.opts = { reducedMotion, showLabels: labels, ambient, prelaunch };
  }, [reducedMotion, labels, ambient, prelaunch]);

  useEffect(() => { if (renderer.current) renderer.current.steeringPreview = steeringPreview ?? null; }, [steeringPreview]);

  useEffect(() => {
    renderer.current?.setView(view);
  }, [view]);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let lastAnn = 0;
    const loop = (t: number) => {
      const dt = (t - last) / 1000;
      last = t;
      const r = renderer.current;
      if (r && !paused) r.frame(dt);
      if (r && r.announcements.length > lastAnn) {
        lastAnn = r.announcements.length;
        setAnnounce(r.announcements[r.announcements.length - 1]);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const aria =
    label ??
    `Overhead course. ${view.run ? `Run ${view.run.id}, step ${view.steps.length} of up to ${view.run.maxSteps}.` : 'No run in progress.'} Buys push RUNNER right toward Harvest; sells push left toward Forge.`;
  return (
    <div ref={wrap} className={className} style={{ position: 'relative' }}>
      <canvas ref={ref} onPointerUp={(e) => {
        if (!onAim || e.button !== 0) return;
        const bounds = e.currentTarget.getBoundingClientRect();
        const x = renderer.current?.aimAt(e.clientX - bounds.left);
        if (x !== undefined) onAim(x);
      }} role="img" aria-label={aria} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div className="sr-only" aria-live="polite">
        {announce}
      </div>
      {paused && <div className="canvas-paused">{pausedLabel}</div>}
    </div>
  );
});
